// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { AttributeGroup } from "@eisinc/App/Store/Reducers/ProductDetails/state";
import { getProductAttributeGroups } from "@eisinc/App/Store/Selectors/ProductDetailsSelectors";
import { AccordionSectionPresentationProps } from "@eisinc/MobiusOverrides/AccordionSection/AccordionSection";
import DataTable from "@eisinc/MobiusOverrides/DataTable/DataTable";
import { XProductModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // XNG-Change
    table?: InjectableCss;
    valueCell?: InjectableCss;
    labelCell?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    accordion?: AccordionSectionPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = (
    // XNG-Changes: read Product only
    { product },
) => {
    // XNG-Changes: get grouped documnets
    const groupedAttributes = useAppSelector(state => getProductAttributeGroups(state as CustomAppState, product));

    // XNG-Change
    const { isLoading } = product as XProductModel;

    // XNG-Change
    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Skeleton height="350px" />
            </StyledWrapper>
        );
    }

    // XNG-Changes: remove !brand + check groupedAttributes
    if (!groupedAttributes || groupedAttributes.length === 0) {
        return null;
    }

    // const limitedAttributeTypes = (attributeTypes?.filter(attributeType => attributeType.includeOnProduct) ?? []).slice(
    //     0,
    //     5,
    // );

    // XNG-Changes: Make it accordion and Remove brand + implement grouping
    return (
        <>
            <ManagedAccordionSection
                title={translate("Attributes")}
                initialExpanded={true}
                data-test-selector="productDetails_specifications_attributes"
                {...styles.accordion}
            >
                {groupedAttributes.map((group: AttributeGroup) => (
                    <StyledWrapper key={group.GroupName} {...styles.wrapper}>
                        <Typography variant="h4" {...styles.attributeTypeLabelText}>
                            {group.GroupName}
                        </Typography>
                        <DataTable {...styles.table}>
                            <DataTableBody>
                                {group.GroupedAttributeTypes.map((attributeType: any) => (
                                    <DataTableRow
                                        data-test-selector="attributes_item"
                                        key={attributeType.Id.toString()}
                                    >
                                        <DataTableCell {...styles.labelCell} data-test-selector="attributes_item_label">
                                            <Typography {...styles.label}>
                                                {attributeType.Label || attributeType.name}
                                            </Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.valueCell} data-test-selector="attributes_item_value">
                                            <Typography {...styles.value}>
                                                {(attributeType.AttributeValues ?? [])
                                                    .map((attributeValue: any) => attributeValue.ValueDisplay)
                                                    .join(", ")}
                                            </Typography>
                                        </DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                ))}
            </ManagedAccordionSection>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
